import { ReactNode, useState } from "react";
import {
  BookTextIcon,
  ChartColumnIcon,
  InboxIcon,
  MessageSquareTextIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ABACUS } from "@/app/_/marketing/hairline/abacus";
import { DRAFTS } from "@/app/_/marketing/hairline/drafts";
import { GEARS } from "@/app/_/marketing/hairline/gears";
import { Hairline, HairlineFigure } from "@/app/_/marketing/hairline/Hairline";
import { KEYPAD } from "@/app/_/marketing/hairline/keypad";
import { KEYRING } from "@/app/_/marketing/hairline/keyring";
import { LEDGER } from "@/app/_/marketing/hairline/ledger";
import { SHELF } from "@/app/_/marketing/hairline/shelf";
import { WHEEL } from "@/app/_/marketing/hairline/wheel";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { Navigation } from "@/constants";
import { ScreenshotSectionType } from "@/layouts/@types";
import { LayoutRouter } from "@/layouts/LayoutRouter";
import {
  ScreenshotSection,
  ScreenshotTopSection,
} from "@/layouts/PublicLayout/sections/ScreenshotSection";

/** A hairline across the page, between one band and the next. */
const RULE = "[border-top-width:1px] [border-top-style:solid] border-border";
/** The page's column: as wide as the topbar's, with the phone's gutter. */
const COLUMN = "w-full max-w-350 ml-auto mr-auto pl-4 pr-4 md:pl-8 md:pr-8";
/** A small label in the figures' own voice: monospaced, quiet. */
const MONO =
  "font-mono text-[11px] leading-none tracking-[0.02em] text-muted-foreground/80";

const STEPS = [
  {
    title: "Connected",
    description:
      "Link your banks and mailboxes. Payments arrive on their own, and so do the receipts, bills and invoices behind them.",
  },
  {
    title: "Proposed",
    description:
      "Agents match each payment to its document and book it the way the payments like it were booked, each with its reasons.",
  },
  {
    title: "Confirmed",
    description:
      "You confirm, adjust or turn it down. Nothing reaches the ledger until you do, and anything can be undone.",
  },
];

/** A band of the page: a figure on its plate, and what it stands for. */
type Band = {
  figure: HairlineFigure;
  label: string;
  hint: string;
  eyebrow: string;
  title: string;
  body: string;
  checks: string[];
  link: { label: string; path: string };
};

const BANDS: Band[] = [
  {
    figure: LEDGER,
    label: "the close",
    hint: "Move across the book to open a month.",
    eyebrow: "The close",
    title: "Every month, booked and balanced.",
    body: "Bank activity from every account lands in one queue, already matched to its receipt or bill with the evidence beside it. Work through it from the keyboard, and the month closes as you go.",
    checks: [
      "Matched to the document it pays",
      "Booked like the payments before it",
      "Explained, and reversible, before it is booked",
    ],
    link: { label: "See the triage queue →", path: "/platform/triage" },
  },
  {
    figure: DRAFTS,
    label: "the review",
    hint: "Move down the board: every draft above your finger settles into its row.",
    eyebrow: "Review",
    title: "Nothing touches the ledger without you.",
    body: "Agents stage their work as drafts: matched receipts, categories, splits, each with the reason it was chosen. Ask for a change in plain English and it arrives the same way. Approve what's right, adjust what isn't, and undo any of it.",
    checks: [
      "A draft until you confirm it",
      "Every change with its reason",
      "Undone as easily as it was made",
    ],
    link: { label: "See how review works →", path: "/platform/triage" },
  },
  {
    figure: SHELF,
    label: "every business",
    hint: "Point at a ledger to pull it out.",
    eyebrow: "Every business",
    title: "One shelf for every set of books.",
    body: "Run every company you own, and your own money, in one workspace. Say a payment was for another business and it is written in both books, with what one owes the other.",
    checks: [
      "Each business keeps its own accounts and rules",
      "A move is booked on both sides at once",
      "Side by side, or one at a time",
    ],
    link: { label: "See multi-entity →", path: "/platform/multi-entity" },
  },
  {
    figure: GEARS,
    label: "in step",
    hint: "Circle the big gear: the whole train turns with it.",
    eyebrow: "Agents",
    title: "Agents that work in step.",
    body: "Ask for a report, a reconciliation or an invoice draft, and agents coordinate across threads and workflows to get it done. Every action is logged, explained and reversible.",
    checks: [
      "Ask in plain English",
      "Teach it once, and it holds",
      "Every proposal comes with its reason",
    ],
    link: { label: "See agentic accounting →", path: "/platform/agents" },
  },
];

/** The features with a figure of their own, drawn on plates above the rest. */
const FIGURE_FEATURES = [
  {
    figure: KEYPAD,
    label: "keyboard first",
    hint: "Press a key: the keys round it dip.",
    title: "Keyboard first",
    description:
      "Arrow through the queue and press a key to book, match or hold. The next payment is already waiting.",
  },
  {
    figure: WHEEL,
    label: "every report",
    hint: "Point at a slice to pull it out.",
    title: "Reports that stay live",
    description:
      "P&L, balance sheet and cash flow, sliced by business, category or month, and shared with a link.",
  },
  {
    figure: KEYRING,
    label: "who holds what",
    hint: "Point at a key to lift it off the ring.",
    title: "A key for every role",
    description:
      "Owners, admins, accountants, bookkeepers and viewers each hold what their role allows, checked on every change. Your accountant's firm holds what you share.",
  },
];

const FEATURES = [
  {
    title: "Documents from your mailbox",
    description:
      "Connect Gmail or Outlook, and the receipts a payment is missing are found where they already are.",
  },
  {
    title: "Rules that learn from your books",
    description:
      "A new payment is booked as the payments from the same payee were. Put one right, and the next follows it.",
  },
  {
    title: "A record of every change",
    description:
      "Who changed what, and when, whether a person or an agent made it, on every record in the books.",
  },
];

const TOUR = {
  type: "screenshot",
  title: "landing-tour",
  tabs: [
    { title: "Triage", imageName: "triage-reconcile", ReactIcon: InboxIcon },
    {
      title: "Threads",
      imageName: "thread-conversation",
      ReactIcon: MessageSquareTextIcon,
    },
    {
      title: "Ledger",
      imageName: "transactions-ledger",
      ReactIcon: BookTextIcon,
    },
    {
      title: "Reports",
      imageName: "reports-dashboard",
      ReactIcon: ChartColumnIcon,
    },
  ],
} as ScreenshotSectionType;

/**
 * A figure on its plate: its name in one corner and, in the other, what it
 * says is under the pointer, live. It plays its own tour until a hand comes.
 */
function Plate({
  figure,
  label,
  hint,
  className,
}: {
  figure: HairlineFigure;
  label: string;
  hint: string;
  className?: string;
}) {
  const [reading, setReading] = useState("rest");

  return (
    <figure
      className={cn(
        "relative m-0 w-full overflow-hidden rounded-xl [border-width:1px] [border-style:solid] border-border bg-background",
        className,
      )}
    >
      <span className={cn(MONO, "absolute left-4 top-4 z-1")}>{label}</span>
      <span
        className={cn(
          MONO,
          "absolute right-4 top-4 z-1 text-foreground [font-variant-numeric:tabular-nums]",
        )}
      >
        {reading}
      </span>
      <Hairline figure={figure} isPlaying onRead={setReading} />
      <figcaption
        className={cn(
          MONO,
          RULE,
          "flex items-start gap-2 pl-4 pr-4 pt-3 pb-3 leading-[1.5]",
        )}
      >
        <span
          aria-hidden
          className="inline-block shrink-0 w-1.5 h-1.5 mt-[0.4em] rounded-full bg-foreground"
        />
        {hint}
      </figcaption>
    </figure>
  );
}

/** A band's opening words: a quiet label, a heading, and what it means. */
function Heading({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-4">
      <span className={MONO}>{eyebrow}</span>
      <Typography
        level="h2"
        className="[line-height:1.06] [letter-spacing:-0.025em] text-[2rem] md:text-[2.75rem] text-balance"
      >
        {title}
      </Typography>
      {children ? (
        <Typography
          level="body-lg"
          className="text-muted-foreground max-w-120 text-pretty"
        >
          {children}
        </Typography>
      ) : null}
    </div>
  );
}

/** What a band's figure stands for, a line each, ruled like a ledger. */
function Checks({ items }: { items: string[] }) {
  return (
    <ul className="m-0 p-0 list-none w-full max-w-120">
      {items.map((item, i) => (
        <li
          key={item}
          className={cn(
            RULE,
            "flex items-baseline gap-4 pt-3 pb-3 last:[border-bottom-width:1px] last:[border-bottom-style:solid] last:border-border",
          )}
        >
          <span className={MONO}>{String(i + 1).padStart(2, "0")}</span>
          <Typography level="body-md">{item}</Typography>
        </li>
      ))}
    </ul>
  );
}

/** A band: its figure's plate on one side, its words on the other, the plate under the words on a phone. */
function BandSection({
  band,
  isPlateFirst,
}: {
  band: Band;
  isPlateFirst: boolean;
}) {
  const navigate = useNavigate();

  return (
    <section className={RULE}>
      <div
        className={cn(
          COLUMN,
          "grid grid-cols-[minmax(0,1fr)] items-center gap-10 pt-16 pb-16 md:pt-28 md:pb-28 lg:gap-20",
          isPlateFirst
            ? "lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]"
            : "lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]",
        )}
      >
        <Plate
          figure={band.figure}
          label={band.label}
          hint={band.hint}
          className={cn("order-2", isPlateFirst && "lg:order-1")}
        />
        <div
          className={cn(
            "order-1 flex flex-col items-start gap-8",
            isPlateFirst && "lg:order-2",
          )}
        >
          <Heading eyebrow={band.eyebrow} title={band.title}>
            {band.body}
          </Heading>
          <Checks items={band.checks} />
          <Button
            variant="ghost"
            size="lg"
            onClick={() => navigate(band.link.path)}
            className="pl-0 pr-0"
          >
            {band.link.label}
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function Page() {
  const navigate = useNavigate();

  const actions = (
    <div className="flex flex-wrap gap-3">
      <Button
        size="lg"
        onClick={() => navigate(Navigation.ROUTES.AUTH.SIGNUP)}
        className="pl-5 pr-5"
      >
        Get started
      </Button>
      <Button
        variant="outline"
        size="lg"
        onClick={() => navigate("/company/contact")}
        className="pl-5 pr-5"
      >
        Talk to us →
      </Button>
    </div>
  );

  return (
    <LayoutRouter
      isPublicLayout
      isHorizontalPaddingIncluded={false}
      isVerticalPaddingIncluded={false}
      isFooterVisible
      isRulersIncluded={false}
      isFooterBorderHidden
      topbarMaxWidth="xl"
      backgroundColor={"var(--background)"}
    >
      {/* The books, counted */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 [background-image:radial-gradient(var(--border)_1px,transparent_1px)] [background-size:20px_20px] [mask-image:radial-gradient(ellipse_at_70%_40%,black,transparent_70%)]"
        />
        <div
          className={cn(
            COLUMN,
            "relative grid grid-cols-[minmax(0,1fr)] items-center gap-10 pt-12 pb-16 md:pt-20 md:pb-24 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-16",
          )}
        >
          <div className="flex flex-col items-start gap-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(Navigation.ROUTES.AUTH.WAITLIST)}
              className="rounded-full pl-3 pr-3 bg-background"
            >
              <span
                aria-hidden
                className="inline-block w-1.5 h-1.5 rounded-full bg-foreground animate-pulse"
              />
              Private beta — request access →
            </Button>
            <Typography
              level="h1"
              className="[line-height:1.02] [letter-spacing:-0.035em] text-[2.75rem] sm:text-[3.5rem] lg:text-[4.25rem] text-balance"
            >
              Your books, closed by agents.{" "}
              <span className="text-muted-foreground/80">Checked by you.</span>
            </Typography>
            <Typography
              level="body-lg"
              className="text-muted-foreground max-w-130 text-pretty"
            >
              Neoledger runs the close across every business you own:
              categorizing, matching receipts, reconciling, drafting entries.
              Every change is proposed with its reasons, and nothing touches the
              ledger until you say so.
            </Typography>
            {actions}
            <Button
              variant="ghost"
              onClick={() => navigate("/story")}
              className="pl-0 pr-0 -mt-2"
            >
              Walk through a month →
            </Button>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2">
              {["Bank feeds", "Gmail & Outlook", "Every business you run"].map(
                (fact, i) => (
                  <span
                    key={fact}
                    className={cn(MONO, "flex items-center gap-4")}
                  >
                    {i > 0 ? (
                      <span
                        aria-hidden
                        className="hidden sm:inline-block w-px h-3 bg-border"
                      />
                    ) : null}
                    {fact}
                  </span>
                ),
              )}
            </div>
          </div>
          <Plate
            figure={ABACUS}
            label="the books"
            hint="Run a finger along a rod: the beads part where you touch."
          />
        </div>
      </section>

      {/* How a month closes */}
      <section className={RULE}>
        <div
          className={cn(
            COLUMN,
            "grid grid-cols-[minmax(0,1fr)] md:grid-cols-3",
          )}
        >
          {STEPS.map((step, i) => (
            <div
              key={step.title}
              className={cn(
                "flex flex-col gap-3 pt-8 pb-8 md:pt-10 md:pb-10 md:pl-8 md:pr-8 first:md:pl-0",
                i > 0 &&
                  "[border-top-width:1px] [border-top-style:solid] border-border md:[border-top-width:0] md:[border-left-width:1px] md:[border-left-style:solid]",
              )}
            >
              <span className={MONO}>{String(i + 1).padStart(2, "0")}</span>
              <Typography level="title-lg">{step.title}</Typography>
              <Typography
                level="body-md"
                className="text-muted-foreground text-pretty"
              >
                {step.description}
              </Typography>
            </div>
          ))}
        </div>
      </section>

      {BANDS.map((band, i) => (
        <BandSection
          key={band.eyebrow}
          band={band}
          isPlateFirst={i % 2 === 0}
        />
      ))}

      {/* The app itself */}
      <section className={cn(RULE, "bg-muted pt-16 pb-16 md:pt-28 md:pb-28")}>
        <div className={cn(COLUMN, "flex flex-col items-center gap-8")}>
          <div className="flex flex-col items-center gap-4 text-center">
            <span className={MONO}>The app</span>
            <Typography
              level="h2"
              className="[line-height:1.06] [letter-spacing:-0.025em] text-[2rem] md:text-[2.75rem]"
            >
              See it working.
            </Typography>
            <Typography
              level="body-lg"
              className="text-muted-foreground max-w-120"
            >
              Recordings of the app itself, played back live: the queue, a
              thread with the agent, the ledger and the reports.
            </Typography>
          </div>
          <ScreenshotTopSection layout={TOUR} maxWidth="xl" />
          <div className="w-full max-w-300">
            <ScreenshotSection layout={TOUR} maxWidth="xl" />
          </div>
        </div>
      </section>

      {/* The rest of the back office */}
      <section className={cn(RULE, "pt-16 md:pt-28")}>
        <div className={cn(COLUMN, "flex flex-col gap-12")}>
          <Heading
            eyebrow="And the rest"
            title="The back office, without the busywork."
          />
          <div className="grid grid-cols-[minmax(0,1fr)] gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {FIGURE_FEATURES.map((feature, i) => (
              <div key={feature.title} className="flex flex-col gap-3">
                <Plate
                  figure={feature.figure}
                  label={feature.label}
                  hint={feature.hint}
                  className="mb-3"
                />
                <span className={MONO}>{String(i + 1).padStart(2, "0")}</span>
                <Typography level="title-md">{feature.title}</Typography>
                <Typography
                  level="body-md"
                  className="text-muted-foreground text-pretty"
                >
                  {feature.description}
                </Typography>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)] gap-px sm:grid-cols-2 lg:grid-cols-3 [border-width:1px] [border-style:solid] border-border bg-border rounded-xl overflow-hidden">
            {FEATURES.map((feature, i) => (
              <div
                key={feature.title}
                className="flex flex-col gap-3 p-6 md:p-8 bg-background [transition:background-color_0.2s] hover:bg-muted"
              >
                <span className={MONO}>
                  {String(FIGURE_FEATURES.length + i + 1).padStart(2, "0")}
                </span>
                <Typography level="title-md">{feature.title}</Typography>
                <Typography
                  level="body-md"
                  className="text-muted-foreground text-pretty"
                >
                  {feature.description}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="pt-16 md:pt-28">
        <div className={cn(COLUMN, "flex flex-col gap-8")}>
          <span className={MONO}>Who it's for</span>
          <div className="grid grid-cols-[minmax(0,1fr)] sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Navigation.SOLUTION_ITEMS.map((solution) => (
              <button
                key={solution.path}
                type="button"
                onClick={() => navigate(solution.path)}
                className="group flex flex-col items-start gap-2 text-left cursor-pointer p-5 rounded-lg [border-width:1px] [border-style:solid] border-border bg-background [transition:border-color_0.2s,background-color_0.2s] hover:border-muted-foreground/80 hover:bg-muted"
              >
                <span className="flex w-full items-center justify-between gap-2">
                  <Typography level="title-md">{solution.name}</Typography>
                  <span
                    aria-hidden
                    className="text-muted-foreground/80 [transition:transform_0.2s] group-hover:transform-[translateX(3px)]"
                  >
                    →
                  </span>
                </span>
                <Typography level="body-sm" className="text-muted-foreground">
                  {solution.tagline}
                </Typography>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Start */}
      <section className="pt-24 pb-24 md:pt-36 md:pb-36">
        <div
          className={cn(COLUMN, "flex flex-col items-center gap-6 text-center")}
        >
          <Typography
            level="h2"
            className="[line-height:1.04] [letter-spacing:-0.03em] text-[2.25rem] md:text-[3.5rem] text-balance max-w-200"
          >
            Start with this month.{" "}
            <span className="text-muted-foreground/80">
              The rest will follow.
            </span>
          </Typography>
          <Typography
            level="body-lg"
            className="text-muted-foreground max-w-120"
          >
            Connect a bank account and watch its payments arrive proposed,
            matched and waiting for you.
          </Typography>
          {actions}
        </div>
      </section>
    </LayoutRouter>
  );
}
